import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { Transaction } from '@olv/ui-shared/models/merchant-transaction.model';
import { ShowMoreComponent } from '../show-more/show-more.component';
import { shortenAddress } from '@olv/ui-shared/utils/shortenAddress.utils';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { StateBadgeComponent } from '../state-badge/state-badge.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MerchantTransactionService } from '@olv/ui-shared/services/merchant-transaction.service';
import { SystemTransactionService } from '@olv/ui-shared/services/system-transaction.service';
import { OtpVerificationDialogComponent } from '../otp-verification-dialog/otp-verification-dialog.component';
import { catchError, finalize, Observable, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { FormatService } from '../../services/format.service';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Scopes, Resources, Resource } from '@olv/ui-shared/models/settlement-permissions.model';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { EmptyStateComponent } from '../empty-state/empty-state.component';

export interface TransactionTableConfig {
  showLoadMore?: boolean;
  showEmptyState?: boolean;
  loadMoreText?: string;
  loadingText?: string;
  retryText?: string;
  columns?: string[];
}

@Component({
  selector: 'app-transactions-table',
  standalone: true,
  imports: [
    MatTooltipModule,
    MatIconModule,
    ShowMoreComponent,
    LoadingSpinnerComponent,
    StateBadgeComponent,
    HasPermissionDirective,
    EmptyStateComponent,
  ],
  templateUrl: './transactions-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./transactions-table.component.scss'],
})
export class TransactionsTableComponent implements OnInit, OnChanges {
  @Input() transactions: Transaction[] = [];
  @Input() initialLoading = false;
  @Input() isLoading = false;
  @Input() error: string | null = null;
  @Input() hasMoreTransactions = false;
  @Input() selectedTransaction: Transaction | null = null;
  @Input() filtersApplied = false;
  @Input() searchText = '';
  @Input() config: TransactionTableConfig = {};

  @Output() transactionClick = new EventEmitter<Transaction>();
  @Output() loadMore = new EventEmitter<void>();
  @Output() retry = new EventEmitter<void>();

  protected readonly formatDateStandard = formatDateStandard;
  protected readonly shortenAddress = shortenAddress;
  protected readonly Scopes = Scopes;
  protected readonly manualRateTooltip = 'Exchange rate used from override value.';
  protected readonly pendingRateTooltip = 'Awaiting an exchange rate.';
  private readonly appCtx = inject(AppContextService);
  private readonly dialog = inject(MatDialog);
  private readonly merchantTransactionService = inject(MerchantTransactionService);
  private readonly systemTransactionService = inject(SystemTransactionService);
  private readonly router = inject(Router);
  private formatService = inject(FormatService);
  private readonly toastr = inject(ToastService);

  isSettlementApp = false;
  defaultConfig: TransactionTableConfig = {
    showLoadMore: true,
    showEmptyState: true,
    loadMoreText: 'Show more',
    loadingText: 'Loading...',
    retryText: 'Retry',
    columns: ['Direction', 'Sending Wallet', 'Receiving Wallet', 'Cryptocurrency', 'Amount', 'Date', 'Status', ''],
  };

  mergedConfig: TransactionTableConfig = {};
  showActionColumn = false;

  ngOnInit(): void {
    const appName = this.appCtx.getAppName();
    this.isSettlementApp = appName === 'settlement';

    const url = this.router.url;
    this.showActionColumn = url.startsWith('/merchants') || url.startsWith('/dashboard');

    this.updateConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.updateConfig();
    }
  }

  private updateConfig(): void {
    const baseConfig = { ...this.defaultConfig, ...this.config };

    if (this.isSettlementApp) {
      baseConfig.columns = [...(baseConfig.columns ?? [])];
      if (!baseConfig.columns.includes('Risk Level')) {
        baseConfig.columns.splice(6, 0, 'Risk Level');
      }
    } else {
      baseConfig.columns = (baseConfig.columns ?? []).filter((column) => column !== 'Risk Level');
    }
    if (this.showActionColumn) {
      if (!baseConfig.columns?.includes('')) {
        baseConfig.columns = [...(baseConfig.columns ?? []), ''];
      }
    } else {
      baseConfig.columns = (baseConfig.columns ?? []).filter((c) => c !== '');
    }

    this.mergedConfig = baseConfig;
  }

  get hasTransactions(): boolean {
    return this.transactions.length > 0;
  }

  get hasRiskColumn(): boolean {
    return this.mergedConfig.columns?.includes('Risk Level') ?? false;
  }

  getIsExchange(direction: string | null | undefined): boolean {
    const normalized = direction?.toLowerCase();
    return normalized === 'buy' || normalized === 'sell';
  }

  getDirectionIcon(transactionDirection: string | null | undefined): string | null {
    switch (transactionDirection?.toLowerCase()) {
      case 'incoming':
        return 'arrow_downward';
      case 'outgoing':
        return 'arrow_upward';
      case 'self':
        return 'swap_horiz';
      default:
        return null;
    }
  }

  getTransactionResource(transaction: Transaction): Resource {
    return transaction?.merchantId ? Resources.MerchantTransactions : Resources.SystemTransactions;
  }

  onTransactionClick(transaction: Transaction): void {
    this.transactionClick.emit(transaction);
  }

  onLoadMore(): void {
    if (!this.isLoading && this.hasMoreTransactions) {
      this.loadMore.emit();
    }
  }

  onRetry(): void {
    this.retry.emit();
  }

  openOtp(action: 'speedup' | 'cancel', transaction: Transaction): void {
    const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        title: action === 'speedup' ? 'Verify Speed Up' : 'Verify Cancel',
      },
    });

    const sub = dialogRef.componentInstance.submitOtp.subscribe((otp: string) => {
      this.handleOtpAction(action, otp, transaction, dialogRef);
    });

    dialogRef.afterClosed().subscribe(() => sub.unsubscribe());
  }

  private handleOtpAction(
    action: 'speedup' | 'cancel',
    otp: string,
    transaction: Transaction,
    dialogRef: MatDialogRef<OtpVerificationDialogComponent>,
  ): void {
    const dialog = dialogRef.componentInstance;

    dialog.setLoadingState(true);
    dialogRef.disableClose = true;

    let request$: Observable<unknown>;

    if (action === 'speedup') {
      request$ = this.getSpeedUpObservable(transaction, otp);
    } else {
      request$ = this.getCancelObservable(transaction, otp);
    }

    request$
      .pipe(
        finalize(() => {
          dialog.setLoadingState(false);
          dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: () => dialogRef.close(),
        error: (error) => {
          if (error?.isOtpError) {
            dialog.setErrorState(error.error?.message);
            dialog.otpForm.reset();
            dialog.focusInput(0);
            return;
          }

          dialogRef.close();
          console.error(error);
        },
      });
  }

  private getCancelObservable(transaction: Transaction, otp: string) {
    const isSystem = !transaction.merchantId;
    const req = { totp: otp };

    const request$ = isSystem
      ? this.systemTransactionService.cancelTransaction(transaction.id, req)
      : this.merchantTransactionService.cancelTransaction(transaction.id, req);

    return request$.pipe(
      tap(() => {
        this.toastr.success('Transaction cancelled successfully');
      }),
      catchError((err) => {
        this.toastr.error('Failed to cancel transaction');
        return throwError(() => err);
      }),
    );
  }

  private getSpeedUpObservable(transaction: Transaction, otp: string) {
    const isSystem = !transaction.merchantId;
    const req = { totp: otp };

    const request$ = isSystem
      ? this.systemTransactionService.speedupTransaction(transaction.id, req)
      : this.merchantTransactionService.speedupTransaction(transaction.id, req);

    return request$.pipe(
      tap(() => {
        this.toastr.success('Transaction sped up successfully');
      }),
      catchError((err) => {
        this.toastr.error('Failed to speed up transaction');
        return throwError(() => err);
      }),
    );
  }

  formatAmount(transaction: Transaction): string {
    if (!transaction) return '0';
    const precision = transaction.amountDisplayPrecision;
    const amount = transaction.amount ?? 0;

    return this.formatService.formatCoinCurrency(amount, precision);
  }
}
